import { z } from 'zod';
import { apiRequest } from './core/client';
import { ContactSchema, parseWithSchema, parseArrayWithSchema, type Contact } from './schemas';

// Extended schema for block contact response which includes move info
const BlockContactResponseSchema = ContactSchema.extend({
  movedEmailIds: z.array(z.string()),
  movedCount: z.number(),
  targetFolderPath: z.string().nullable(),
});

// Schema for bulk contact operations.
// contacts.id is a Postgres bigint, which node-postgres surfaces as a string
// (to avoid >2^53 precision loss) — so the API returns contactIds as strings,
// matching ContactSchema.id (z.string()). coerce keeps it robust if a caller
// ever sends numbers. The earlier z.array(z.number()) rejected the real string
// payload, throwing AFTER the block had already committed server-side — which
// surfaced as a false "N senders failed to block — restored" toast.
const BulkContactResponseSchema = z.object({
  updated: z.number(),
  contactIds: z.array(z.coerce.string()),
});

export type { Contact };

// ============================================================================
// Contact Fetching
// ============================================================================

export interface FetchContactsOptions {
  search?: string;
  limit?: number;
  offset?: number;
  approvedState?: string;
  activeSince?: string;
  includePending?: boolean;
  pendingLimit?: number;
  pendingActiveSince?: string;
  signal?: AbortSignal;
}

export async function fetchContacts(
  accountId: string,
  options: FetchContactsOptions = {}
): Promise<Contact[]> {
  const data = await apiRequest<unknown[]>({
    path: '/contacts',
    accountId,
    query: {
      q: options.search,
      limit: options.limit,
      offset: options.offset,
      approved_state: options.approvedState,
      active_since: options.activeSince,
      include_pending: options.includePending,
      pending_limit: options.pendingLimit,
      pending_active_since: options.pendingActiveSince,
    },
    signal: options.signal,
  });
  return parseArrayWithSchema(ContactSchema, data, 'fetchContacts');
}

// ============================================================================
// Contact Actions
// ============================================================================

export async function approveContact(accountId: string, contactId: string): Promise<Contact> {
  const data = await apiRequest<unknown>({
    path: `/contacts/${encodeURIComponent(contactId)}/approve`,
    method: 'POST',
    accountId,
  });
  return parseWithSchema(ContactSchema, data, 'approveContact');
}

export async function updateContactNotifications(
  accountId: string,
  contactId: string,
  notificationsEnabled: boolean
): Promise<Contact> {
  const data = await apiRequest<unknown>({
    path: `/contacts/${encodeURIComponent(contactId)}/notifications`,
    method: 'PATCH',
    accountId,
    body: { notificationsEnabled },
  });
  return parseWithSchema(ContactSchema, data, 'updateContactNotifications');
}

export interface BlockContactResult {
  contact: Contact;
  movedEmailIds: string[];
  movedCount: number;
  targetFolderPath: string | null;
}

export async function blockContact(
  accountId: string,
  contactId: string
): Promise<BlockContactResult> {
  const data = await apiRequest<unknown>({
    path: `/contacts/${encodeURIComponent(contactId)}/block`,
    method: 'POST',
    accountId,
  });

  const parsed = parseWithSchema(BlockContactResponseSchema, data, 'blockContact');

  return {
    contact: parsed,
    movedEmailIds: parsed.movedEmailIds,
    movedCount: parsed.movedCount,
    targetFolderPath: parsed.targetFolderPath,
  };
}

// ============================================================================
// Bulk Contact Actions
// ============================================================================

export interface BulkContactResult {
  updated: number;
  contactIds: string[];
}

export async function bulkBlockContacts(
  accountId: string,
  contactIds: (string | number)[]
): Promise<BulkContactResult> {
  const data = await apiRequest<unknown>({
    path: '/contacts/bulk/block',
    method: 'POST',
    accountId,
    body: { contactIds },
  });
  return parseWithSchema(BulkContactResponseSchema, data, 'bulkBlockContacts');
}

export async function bulkApproveContacts(
  accountId: string,
  contactIds: (string | number)[]
): Promise<BulkContactResult> {
  const data = await apiRequest<unknown>({
    path: '/contacts/bulk/approve',
    method: 'POST',
    accountId,
    body: { contactIds },
  });
  return parseWithSchema(BulkContactResponseSchema, data, 'bulkApproveContacts');
}
